import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';

export default function Login() {
  const privacyPolicyUrl = 'https://flyingbasket.com/privacy-policy';
  const { login, register, setLanguage, language } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ email:'', password:'', full_name:'', company:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (isRegister) {
        await register(form);
        navigate('/dashboard');
      } else {
        const user = await login(form.email, form.password);
        navigate(user?.role === 'admin' ? '/admin' : '/dashboard');
      }
    } catch(err) {
      setError(err.response?.data?.error || t('login.error'));
    } finally { setLoading(false); }
  };

  const languages = [
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'it', label: 'IT', flag: '🇮🇹' },
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'es', label: 'ES', flag: '🇪🇸' },
    { code: 'de', label: 'DE', flag: '🇩🇪' },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'register') setIsRegister(true);
    if (mode === 'login') setIsRegister(false);
  }, [location.search]);

  if (isMobile) {
    return (
      <div style={{ minHeight:'100vh', background:'linear-gradient(180deg, #1c3177 0%, #2d4eb7 34%, #f4f6fd 34%, #f4f6fd 100%)' }}>
        <div style={{ padding:'1.25rem 1rem 0.75rem', color:'white' }}>
          <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height:34, width:'auto', objectFit:'contain', filter:'brightness(0) invert(1)' }} />
          <h1 style={{ fontFamily:'var(--font-head)', fontSize:'1.8rem', fontWeight:800, lineHeight:1.15, marginTop:'1.1rem' }}>
            {t('login.hero.line1')}<br />{t('login.hero.line2')}<br />{t('login.hero.line3')}
          </h1>
          <p style={{ color:'rgba(255,255,255,0.82)', fontSize:'0.92rem', lineHeight:1.5, marginTop:'0.75rem', maxWidth:420 }}>
            {t('login.hero.subtitle')}
          </p>
        </div>

        <div style={{ padding:'0 1rem 1.25rem' }}>
          <div className="card" style={{ padding:'1rem', marginTop:'1rem' }}>
            <div
              style={{
                display:'flex',
                flexWrap:'wrap',
                gap:'0.4rem',
                background:'var(--surface-low)',
                border:'1px solid var(--surface-high)',
                borderRadius:10,
                padding:'0.35rem',
                marginBottom:'1rem'
              }}
            >
              {languages.map(({ code, label, flag }) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  style={{
                    display:'flex',
                    alignItems:'center',
                    gap:'0.35rem',
                    background:language === code ? 'white' : 'transparent',
                    border:'none',
                    borderRadius:8,
                    padding:'0.45rem 0.6rem',
                    fontSize:'0.78rem',
                    fontWeight:700,
                    cursor:'pointer',
                    color:language === code ? 'var(--primary)' : 'var(--on-surface)'
                  }}
                >
                  <span style={{ fontSize:'1rem' }}>{flag}</span>
                  {label}
                </button>
              ))}
            </div>

            <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height:60, width:'auto', objectFit:'contain', marginBottom:'1rem' }} />
            <h2 style={{ fontFamily:'var(--font-head)', fontSize:'1.45rem', fontWeight:800, marginBottom:'0.45rem', color:'var(--primary)' }}>
              {t('login.title')}
            </h2>
            <p style={{ color:'var(--on-surface-muted)', marginBottom:'1.25rem', fontSize:'0.92rem', lineHeight:1.5 }}>
              {t('login.subtitle')}
            </p>

            {error && (
              <div style={{ background:'#fee2e2', color:'#dc2626', padding:'0.75rem 1rem', borderRadius:'var(--radius)', marginBottom:'1rem', fontSize:'0.875rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handle}>
              {isRegister && <>
                <label className="label">{t('login.fields.fullName')}</label>
                <input className="input" style={{ marginBottom:'1rem' }} value={form.full_name} onChange={e => setForm({...form, full_name:e.target.value})} placeholder={t('login.placeholders.fullName')} />
                <label className="label">{t('login.fields.company')}</label>
                <input className="input" style={{ marginBottom:'1rem' }} value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder={t('login.placeholders.company')} />
              </>}
              <label className="label">{t('login.fields.email')}</label>
              <input className="input" style={{ marginBottom:'1rem' }} type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder={t('login.placeholders.email')} required />
              <label className="label">{t('login.fields.password')}</label>
              <input className="input" style={{ marginBottom:'1rem' }} type="password" value={form.password} onChange={e => setForm({...form, password:e.target.value})} placeholder="••••••••" required />
              <button className="btn-primary" style={{ width:'100%', justifyContent:'center', padding:'1rem' }} type="submit" disabled={loading}>
                {loading
                  ? t('login.loading')
                  : isRegister
                    ? t('login.ctaRegister')
                    : t('login.ctaLogin')}
              </button>
              {isRegister && (
                <p style={{ marginTop:'0.85rem', fontSize:'0.75rem', lineHeight:1.5, color:'var(--on-surface-muted)', textAlign:'center' }}>
                  {t('login.privacyConsent')}{' '}
                  <a
                    href={privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color:'var(--primary)', textDecoration:'none', fontWeight:600 }}
                  >
                    {t('common.privacyPolicy')}
                  </a>
                </p>
              )}
            </form>

            <p style={{ textAlign:'center', marginTop:'1.25rem', fontSize:'0.875rem', color:'var(--on-surface-muted)', lineHeight:1.5 }}>
              {isRegister ? t('login.haveAccount') : t('login.newUser')}{' '}
              <button onClick={() => setIsRegister(!isRegister)} style={{ background:'none', border:'none', color:'var(--primary)', fontWeight:700, cursor:'pointer' }}>
                {isRegister ? t('login.switchToLogin') : t('login.switchToRegister')}
              </button>
            </p>

            <div style={{ display:'flex', justifyContent:'center', marginTop:'1.1rem', fontSize:'0.78rem', color:'var(--on-surface-muted)' }}>
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color:'inherit', textDecoration:'none', cursor:'pointer' }}
              >
                {t('login.footer.privacy')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:isMobile ? 'column' : 'row', minHeight:'100vh' }}>

      {/* Colonna sinistra */}
      <div style={{ flex:isMobile ? 'none' : 1, minHeight:isMobile ? 260 : 'auto', position:'relative', display:'flex', flexDirection:'column', justifyContent:'space-between', padding:isMobile ? '1.5rem 1rem' : '3rem', color:'white', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'url(/images/drones/login-image.jpg)', backgroundSize:'cover', backgroundPosition:'center', zIndex:0 }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg, rgba(40,65,154,0.38) 0%, rgba(20,35,90,0.92) 100%)', zIndex:1 }} />

        <div style={{ position:'relative', zIndex:2 }}>
          <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height:38, width:'auto', objectFit:'contain', filter:'brightness(0) invert(1)' }} />
        </div>

        <div style={{ position:'relative', zIndex:2 }}>
          <h1 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '1.9rem' : '2.5rem', fontWeight:800, lineHeight:1.2, marginBottom:'1.25rem' }}>
            {t('login.hero.line1')}<br/>{t('login.hero.line2')}<br/>{t('login.hero.line3')}
          </h1>
          <p style={{ color:'rgba(255,255,255,0.75)', fontSize:isMobile ? '0.9rem' : '1rem', maxWidth:400, marginBottom:isMobile ? '1rem' : '2rem' }}>
            {t('login.hero.subtitle')}
          </p>
          <div style={{ display:'flex', flexDirection:isMobile ? 'column' : 'row', gap:isMobile ? '0.75rem' : '1.5rem' }}>
            {[
              { label: t('login.hero.stat1Label'), val:'200 KG' },
              { label: t('login.hero.stat2Label'), val:'30 KM' },
            ].map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,0.12)', borderRadius:12, padding:isMobile ? '0.85rem 1rem' : '1rem 1.5rem', backdropFilter:'blur(16px)', border:'1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize:'0.7rem', opacity:0.7, textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div>
                <div style={{ fontFamily:'var(--font-head)', fontWeight:700, fontSize:'1.25rem' }}>{s.val}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position:'relative', zIndex:2, fontSize:'0.75rem', opacity:0.5 }}>
          © 2025 FlyingBasket SRL. Precision in Flight.
        </div>
      </div>

      {/* Colonna destra */}
      <div style={{ width:isMobile ? '100%' : 500, display:'flex', alignItems:'center', justifyContent:'center', padding:isMobile ? '1rem' : '3rem', background:'white', position:'relative' }}>

        <div
          style={{
            position:isMobile ? 'static' : 'absolute', top:'1.5rem', right:'1.5rem',
            display:'flex', alignItems:'center', gap:'0.4rem',
            background:'var(--surface-low)', border:'1px solid var(--surface-high)',
            borderRadius:8, padding:'0.25rem', marginBottom:isMobile ? '1rem' : 0,
            color:'var(--on-surface)', transition:'all 0.15s'
          }}
        >
          {languages.map(({ code, label, flag }) => (
            <button
              key={code}
              onClick={() => setLanguage(code)}
              style={{
                display:'flex', alignItems:'center', gap:'0.35rem',
                background:language === code ? 'white' : 'transparent',
                border:'none', borderRadius:6, padding:'0.35rem 0.55rem',
                fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                color:language === code ? 'var(--primary)' : 'var(--on-surface)'
              }}
            >
              <span style={{ fontSize:'1rem' }}>{flag}</span>
              {label}
            </button>
          ))}
        </div>

        <div style={{ width:'100%', maxWidth:400 }}>
          <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height:isMobile ? 64 : 80, width:'auto', objectFit:'contain', marginBottom:'1.5rem' }} />
          <h2 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '1.5rem' : '1.75rem', fontWeight:800, marginBottom:'0.5rem', color:'var(--primary)' }}>
            {t('login.title')}
          </h2>
          <p style={{ color:'var(--on-surface-muted)', marginBottom:'2rem', fontSize:'0.9rem' }}>
            {t('login.subtitle')}
          </p>

          {error && (
            <div style={{ background:'#fee2e2', color:'#dc2626', padding:'0.75rem 1rem', borderRadius:'var(--radius)', marginBottom:'1rem', fontSize:'0.875rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handle}>
            {isRegister && <>
              <label className="label">{t('login.fields.fullName')}</label>
              <input className="input" style={{ marginBottom:'1rem' }} value={form.full_name} onChange={e => setForm({...form, full_name:e.target.value})} placeholder={t('login.placeholders.fullName')} />
              <label className="label">{t('login.fields.company')}</label>
              <input className="input" style={{ marginBottom:'1rem' }} value={form.company} onChange={e => setForm({...form, company:e.target.value})} placeholder={t('login.placeholders.company')} />
            </>}
            <label className="label">{t('login.fields.email')}</label>
            <input className="input" style={{ marginBottom:'1rem' }} type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder={t('login.placeholders.email')} required />
            <label className="label">{t('login.fields.password')}</label>
            <input className="input" style={{ marginBottom:'1.5rem' }} type="password" value={form.password} onChange={e => setForm({...form, password:e.target.value})} placeholder="••••••••" required />
            <button className="btn-primary" style={{ width:'100%', justifyContent:'center', padding:'1rem' }} type="submit" disabled={loading}>
              {loading
                ? t('login.loading')
                : isRegister
                  ? t('login.ctaRegister')
                  : t('login.ctaLogin')}
            </button>
            {isRegister && (
              <p style={{ marginTop:'0.85rem', fontSize:'0.75rem', lineHeight:1.5, color:'var(--on-surface-muted)', textAlign:'center' }}>
                {t('login.privacyConsent')}{' '}
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color:'var(--primary)', textDecoration:'none', fontWeight:600 }}
                >
                  {t('common.privacyPolicy')}
                </a>
              </p>
            )}
          </form>

          <p style={{ textAlign:'center', marginTop:'1.5rem', fontSize:'0.875rem', color:'var(--on-surface-muted)' }}>
            {isRegister ? t('login.haveAccount') : t('login.newUser')}{' '}
            <button onClick={() => setIsRegister(!isRegister)} style={{ background:'none', border:'none', color:'var(--primary)', fontWeight:600, cursor:'pointer' }}>
              {isRegister ? t('login.switchToLogin') : t('login.switchToRegister')}
            </button>
          </p>

          <div style={{ display:'flex', justifyContent:'center', marginTop:'2rem', fontSize:'0.75rem', color:'var(--on-surface-muted)' }}>
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color:'inherit', textDecoration:'none', cursor:'pointer' }}
            >
              {t('login.footer.privacy')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
